import mammoth from 'mammoth';

const ANSWER_ORDER = ['A', 'B', 'C', 'D'];

/**
 * Parses a DOCX or plain-text quiz file and extracts questions.
 * @param {ArrayBuffer} arrayBuffer
 * @returns {Promise<Array>}
 */
export const parseDocxToQuiz = async (arrayBuffer) => {
  try {
    const bytes = new Uint8Array(arrayBuffer);
    let rawText;

    if (bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4b) {
      const textResult = await mammoth.extractRawText({ arrayBuffer });
      rawText = textResult.value;
    } else {
      rawText = new TextDecoder('utf-8').decode(arrayBuffer);
    }

    const questions = parseQuestionsFromText(rawText);

    if (questions.length === 0) {
      throw new Error('No valid quiz questions found in the document. Please check the format.');
    }

    return questions;
  } catch (error) {
    console.error('Error parsing DOCX:', error);
    throw new Error(
      error.message && error.message.includes('No valid quiz questions')
        ? error.message
        : 'Failed to parse quiz document. The file may be corrupted or in an unsupported format.'
    );
  }
};

const stripBom = (text) => String(text || '').replace(/^\uFEFF/, '');

const isDecorativeLine = (line) =>
  /^(?:[-*_.=]{3,}|[-–—]+)$/.test(line);

/** `{d}` / `{D}` as a standalone answer marker, not `{date}` or similar. */
const BRACE_MARKER = /^\{([a-d])\}(?!\w)\s*(.*)$/i;

const LETTER_PREFIX = /^([a-d])\s*[.):-]\s*(?:\{[a-d]\}(?!\w)\s*)?(.*)$/i;

const QUESTION_LABEL = /^question\s+(\d+)\s*[:.-]\s*(.*)$/i;

const NUMBERED_QUESTION = /^(\d+)\s*[.):]\s*(.*)$/;

// "Answer: A", "Answer: {d}", and restated keys like "Answer: A. R10, R10.1, R10.11"
const CORRECT_KEY =
  /^(?:correct\s+)?(?:answer|ans)\s*(?::|-)\s*(?:\{([a-d])\}(?!\w)|([a-d])\b)(?:\s*[.):-]?\s*.*)?$/i;

const ANSWER_FIELD_IN_LINE =
  /(?:^|\s)((?:correct\s+)?(?:answer|ans)\s*(?::|-)\s*.+)$/i;

const stripLeadingBraceMarker = (text) =>
  String(text || '').replace(/^\{[a-d]\}(?!\w)\s*/i, '').trim();

export const matchAnswerStart = (line) => {
  if (!line) return null;
  const brace = line.match(BRACE_MARKER);
  if (brace) {
    return { id: brace[1].toUpperCase(), text: brace[2].trim() };
  }
  const letter = line.match(LETTER_PREFIX);
  if (letter) {
    return { id: letter[1].toUpperCase(), text: stripLeadingBraceMarker(letter[2]) };
  }
  return null;
};

export const matchQuestionStart = (line) => {
  if (!line) return null;
  // Answers like "A. …" / "{d} …" must not be treated as questions.
  if (matchAnswerStart(line)) return null;
  const labeled = line.match(QUESTION_LABEL);
  if (labeled) {
    return { id: parseInt(labeled[1], 10), text: labeled[2].trim() };
  }
  const numbered = line.match(NUMBERED_QUESTION);
  if (numbered) {
    return { id: parseInt(numbered[1], 10), text: numbered[2].trim() };
  }
  return null;
};

export const matchCorrectKey = (line) => {
  if (!line) return null;
  const match = line.match(CORRECT_KEY);
  if (!match) return null;
  return (match[1] || match[2]).toUpperCase();
};

/**
 * Split a physical line that packed choices and/or Answer: together
 * (common when Word/DOCX extraction drops newlines).
 * "Answer:" is peeled off first so restated keys never become option text.
 */
export const splitQuizContentLine = (line) => {
  const trimmed = String(line || '').trim();
  if (!trimmed) return [];

  let body = trimmed;
  let answerLine = null;
  const answerMatch = trimmed.match(ANSWER_FIELD_IN_LINE);
  if (answerMatch) {
    body = trimmed.slice(0, answerMatch.index).trim();
    answerLine = answerMatch[1].trim();
  }

  const optionText = body
    .replace(/\s+([A-Da-d])\s*[.):-]\s+/g, '\n$1. ')
    .replace(/\s+\{([a-d])\}(?!\w)\s*/gi, '\n{$1} ');

  const parts = optionText
    .split('\n')
    .map((part) => part.trim())
    .filter(Boolean);

  if (answerLine) parts.push(answerLine);
  return parts;
};

const appendText = (existing, extra) => {
  const next = String(extra || '').trim();
  if (!next) return existing || '';
  const prev = String(existing || '').trim();
  if (!prev) return next;
  return `${prev} ${next}`;
};

const upsertAnswer = (question, id, text) => {
  const incoming = stripLeadingBraceMarker(text);
  const existing = question.answers.find((answer) => answer.id === id);
  if (existing) {
    existing.text = appendText(existing.text, incoming);
    return existing;
  }
  const created = { id, text: incoming, isCorrect: false };
  question.answers.push(created);
  return created;
};

const finalizeQuestion = (question) => {
  if (!question) return null;
  const byId = new Map();
  question.answers.forEach((answer) => {
    if (!ANSWER_ORDER.includes(answer.id)) return;
    const prev = byId.get(answer.id);
    if (prev) {
      prev.text = appendText(prev.text, answer.text);
    } else {
      byId.set(answer.id, {
        id: answer.id,
        text: stripLeadingBraceMarker(answer.text),
        isCorrect: false,
      });
    }
  });

  const answers = ANSWER_ORDER
    .filter((id) => byId.has(id))
    .map((id) => {
      const answer = byId.get(id);
      answer.isCorrect = question.correctId === id;
      return answer;
    });

  return {
    id: question.id,
    questionNumber: question.id,
    text: String(question.text || '').trim(),
    answers,
  };
};

/**
 * Parses quiz questions from raw text.
 * Supports A./B./C./D., A) B) C) D), and {a} {b} {c} {d} markers,
 * plus wrapped question/answer lines and blank-line gaps.
 * @param {string} rawText
 * @returns {Array}
 */
export const parseQuestionsFromText = (rawText) => {
  const lines = stripBom(rawText)
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .flatMap(splitQuizContentLine);

  const questions = [];
  let current = null;
  let mode = 'question'; // 'question' | 'answer'
  let currentAnswerId = null;

  const flush = () => {
    const finished = finalizeQuestion(current);
    if (finished && (finished.text || finished.answers.length)) {
      questions.push(finished);
    }
    current = null;
    mode = 'question';
    currentAnswerId = null;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line || isDecorativeLine(line)) continue;

    const questionStart = matchQuestionStart(line);
    if (questionStart) {
      flush();
      current = {
        id: questionStart.id,
        text: questionStart.text,
        answers: [],
        correctId: null,
      };
      mode = 'question';
      currentAnswerId = null;
      continue;
    }

    if (!current) continue;

    const correctId = matchCorrectKey(line);
    if (correctId) {
      current.correctId = correctId;
      mode = 'answer';
      currentAnswerId = null;
      continue;
    }

    const answerStart = matchAnswerStart(line);
    if (answerStart) {
      const answer = upsertAnswer(current, answerStart.id, answerStart.text);
      currentAnswerId = answer.id;
      mode = 'answer';
      continue;
    }

    if (mode === 'answer' && currentAnswerId) {
      upsertAnswer(current, currentAnswerId, line);
    } else {
      current.text = appendText(current.text, line);
    }
  }

  flush();
  return questions;
};
